/**
 * IFAS (funeral administration) API calls.
 *
 * Split out of services/dashboard.ts. Covers cases, body removal and dispatch,
 * mortuary, warehouse, catering, tombstones, memorials, fleet, quotations,
 * revenue and the catalogue. Nothing here calls into claims.
 */

import instance from "./api";
import EndPoints from "./api-endpoints";

export const getAllCases = async (page = 0, size = 10) => {
  try {
    return instance
      .get(EndPoints.getAllCases, {
        params: { page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const searchCases = async ({
  status,
  searchText,
  branchName,
  page = 0,
  size = 10,
}: {
  status?: string | null;
  searchText?: string | null;
  branchName?: string | null;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.searchCases, {
        params: {
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
          ...(branchName ? { branchName } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getAllBodyRemovals = async (page = 0, size = 10) => {
  try {
    return instance
      .get(EndPoints.getAllBodyRemovals, {
        params: { page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const searchBodyRemovals = async ({
  status,
  searchText,
  page = 0,
  size = 10,
}: {
  status?: string;
  searchText?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.searchBodyRemovals, {
        params: {
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getAllQuotations = async (page = 0, size = 10) => {
  try {
    return instance
      .get(EndPoints.getAllQuotations, {
        params: { page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getAllCatering = async (page = 0, size = 10) => {
  try {
    return instance
      .get(EndPoints.getAllCatering, {
        params: { page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const deliverBodyRemoval = async (bodyRemovalNumber: string, payload: any) => {
  try {
    return instance
      .post(
        `${EndPoints.deliverBodyRemoval}/${bodyRemovalNumber}/deliver`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
          },
        }
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getAllTombstones = async (page = 0, size = 10) => {
  try {
    return instance
      .get(EndPoints.getAllTombstones, {
        params: { page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getCateringMenuItems = async (category: string) => {
  try {
    return instance
      .get(EndPoints.getCateringMenuItems, {
        params: { category },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getCaseDetails = async (caseNumber: string) => {
  try {
    return instance
      .get(EndPoints.getCaseDetails + "/" + caseNumber)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getBodyRemovalDetails = async (bodyRemovalNumber: string) => {
  try {
    return instance
      .get(EndPoints.getBodyRemovalDetails + "/" + bodyRemovalNumber)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const updateBodyRemovalDetails = async (
  bodyRemovalNumber: string,
  payload: any,
) => {
  try {
    return instance
      .put(`${EndPoints.getBodyRemovalDetails}/${bodyRemovalNumber}`, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getQuotationDetails = async (quotationNumber: string) => {
  try {
    return instance
      .get(EndPoints.getQuotationDetails + "/" + quotationNumber)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const submitCase = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.submitCase, payload, {
        headers: {
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const assignCase = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.assignCase, payload, {
        headers: {
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getFuneralPlanners = async (branchName?: string | null) => {
  try {
    return instance
      .get(EndPoints.funeralPlanners, {
        params: branchName ? { branchName } : {},
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const configureFuneralPlannerBranch = async (
  plannerUserId: string,
  payload: { branchName: string; configuredByUserId: string },
) => {
  try {
    return instance
      .put(`${EndPoints.funeralPlanners}/${plannerUserId}/branch`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateCase = async (payload: any) => {
  try {
    return instance
      .put(EndPoints.updateCase, payload, {
        headers: {
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getCateringDetails = async (id: string) => {
  try {
    return instance
      .get(EndPoints.getCateringDetails + "/" + id)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getTombstoneDetails = async (id: string) => {
  try {
    return instance
      .get(EndPoints.getTombstoneDetails + "/" + id)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const updateCateringDetails = async (
  cateringId: string,
  userId: string,
  payload: any,
) => {
  try {
    return instance
      .put(`${EndPoints.updateCateringDetails}/${cateringId}`, payload, {
        headers: {
          "user-Id": userId,
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const updateTombstoneDetails = async (
  tombstoneId: string,
  userId: string,
  payload: any,
) => {
  try {
    return instance
      .put(`${EndPoints.updateTombstoneDetails}/${tombstoneId}`, payload, {
        headers: {
          "user-Id": userId,
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const confirmCateringDetails = async (
  cateringId: string,
  userId: string,
) => {
  try {
    return instance
      .post(`${EndPoints.confirmCateringDetails}/${cateringId}/confirm`, null, {
        headers: {
          "user-Id": userId,
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const confirmTombstoneDetails = async (tombstoneId: string, userId: string) => {
  try {
    return instance
      .post(`${EndPoints.confirmTombstoneDetails}/${tombstoneId}/confirm`, null, {
        headers: {
          "user-Id": userId,
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getCaseHistory = async (caseId: any) => {
  try {
    return instance
      .get(EndPoints.getCaseHistory + "/" + caseId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getBodyRemovalHistory = async (bodyRemovalId: string) => {
  try {
    return instance
      .get(EndPoints.getBodyRemovalHistory + "/" + bodyRemovalId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getCateringHistory = async (cateringId: string) => {
  try {
    return instance
      .get(EndPoints.getCateringHistory + "/" + cateringId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getDriverHistory = async (driverId: string) => {
  try {
    return instance
      .get(EndPoints.getDriverHistory + "/" + driverId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getVehicleHistory = async (vehicleId: string) => {
  try {
    return instance
      .get(EndPoints.getVehicleHistory + "/" + vehicleId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const fetchAllVehicles = async (page = 0, size = 10, branchLocation?: string, branchId?: string) => {
  try {
    return instance
      .get(EndPoints.getAllVehicles, {
        params: {
          page,
          size,
          ...(branchId ? { branchId } : {}),
          ...(branchLocation ? { branchLocation } : {}),
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const fetchAllDrivers = async (page = 0, size = 10, branchLocation?: string, branchId?: string) => {
  try {
    return instance
      .get(EndPoints.getAllDrivers, {
        params: {
          page,
          size,
          ...(branchId ? { branchId } : {}),
          ...(branchLocation ? { branchLocation } : {}),
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const fetchAvailableDrivers = async (page = 0, size = 10, branchLocation?: string, branchId?: string) => {
  try {
    return instance
      .get(EndPoints.getAvailableDrivers, {
        params: {
          page,
          size,
          ...(branchId ? { branchId } : {}),
          ...(branchLocation ? { branchLocation } : {}),
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const fetchAvailableVehicles = async (page = 0, size = 10, branchLocation?: string, branchId?: string) => {
  try {
    return instance
      .get(EndPoints.getAvailableVehicles, {
        params: {
          page,
          size,
          ...(branchId ? { branchId } : {}),
          ...(branchLocation ? { branchLocation } : {}),
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const addVehicle = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.addVehicle, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
}

export const addDriver = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.addDriver, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
}

export const getVehicleDetails = async (vehicleID: string) => {
  try {
    return instance
      .get(EndPoints.fetchVehicleDetails + "/" + vehicleID)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
}

export const getDriverDetails = async (driverId: string) => {
  try {
    return instance
      .get(EndPoints.fetchDriverDetails + "/" + driverId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
}

export const updateDriverDetails = async (driverId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.updateDriverDetails}/${driverId}`, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const updateVehicleDetails = async (vehicleId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.updateVehicleDetails}/${vehicleId}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const removeDriver = async (
  driverId: string,
  payload: { status: string; reason: string },
) => {
  try {
    return instance
      .put(`${EndPoints.DeleteDriver}/${driverId}/status`, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const removeVehicle = async (
  vehicleId: string,
  payload: { status: string; reason: string },
) => {
  try {
    return instance
      .put(`${EndPoints.DeleteVehicle}/${vehicleId}/status`, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const assignVehicle = async (vehicleId: string, payload: any) => {
  try {
    return instance
      .post(EndPoints.assignVehicle + "/" + vehicleId, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const assignDriver = async (driverId: string, payload: any) => {
  try {
    return instance
      .post(EndPoints.assignDriver + "/" + driverId, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getDriverAssignments = async (driverId: string) => {
  try {
    return await instance.get(`${EndPoints.driverAssignments}/${driverId}/assignments`);
  } catch (error: any) {
    return error?.response;
  }
};

export const reassignFleetAllocation = async (allocationId: string, payload: any) => {
  try {
    return await instance.post(`${EndPoints.completeFuneralFleetAllocation}/${allocationId}/reassign`, payload);
  } catch (error: any) {
    return error?.response;
  }
};

export const completeFuneralFleetAllocation = async (allocationId: string, payload: any = {}) => {
  try {
    return instance
      .post(`${EndPoints.completeFuneralFleetAllocation}/${allocationId}/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const allocateResources = async (bodyRemovalNumber: any, payload: any) => {
  try {
    return instance
      .post(EndPoints.allocateResources + "/" + bodyRemovalNumber, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
}

export const createRemovalOrder = async (bodyRemovalNumber: string, payload: any) => {
  try {
    return instance
      .post(
        `${EndPoints.createRemovalOrder}/${bodyRemovalNumber}/removal-order`,
        payload,
        {
          headers: {
            Accept: "*/*",
          },
        },
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const acceptBodyRemovalDispatch = async (
  bodyRemovalNumber: string,
  payload: any,
) => {
  try {
    return instance
      .post(
        `${EndPoints.acceptDispatch}/${bodyRemovalNumber}/accept-dispatch`,
        payload,
        {
          headers: {
            Accept: "*/*",
          },
        },
      )
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const markBodyRemovalEnRoute = async (
  bodyRemovalNumber: string,
  payload: any,
) => {
  try {
    return instance
      .post(
        `${EndPoints.markDriverEnRoute}/${bodyRemovalNumber}/en-route`,
        payload,
        {
          headers: {
            Accept: "*/*",
          },
        },
      )
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const markBodyRemovalArrivedAtScene = async (
  bodyRemovalNumber: string,
  payload: any,
) => {
  try {
    return instance
      .post(
        `${EndPoints.markArrivedAtScene}/${bodyRemovalNumber}/arrive-scene`,
        payload,
        {
          headers: {
            Accept: "*/*",
          },
        },
      )
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const confirmBodyCollection = async (
  bodyRemovalNumber: string,
  payload: any,
) => {
  try {
    return instance
      .post(
        `${EndPoints.confirmBodyCollection}/${bodyRemovalNumber}/collect`,
        payload,
        {
          headers: {
            Accept: "*/*",
          },
        },
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const markBodyRemovalArrivedAtMortuary = async (
  bodyRemovalNumber: string,
  payload: any,
) => {
  try {
    return instance
      .post(
        `${EndPoints.markArrivedAtMortuary}/${bodyRemovalNumber}/arrive-mortuary`,
        payload,
        {
          headers: {
            Accept: "*/*",
          },
        },
      )
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const downloadBodyRemovalDocument = async (bodyRemovalNumber: string) => {
  try {
    return instance
      .get(
        `${EndPoints.downloadBodyRemovalDocument}/${bodyRemovalNumber}/document`,
        {
          responseType: "blob",
          headers: {
            Accept: "application/pdf",
          },
        },
      )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getMortuaryRecords = async ({
  status,
  mortuaryName,
  searchText,
  page = 0,
  size = 10,
}: {
  status?: string;
  mortuaryName?: string;
  searchText?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.getMortuaryRecords, {
        params: {
          ...(status ? { status } : {}),
          ...(mortuaryName ? { mortuaryName } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMortuaryRecord = async (mortuaryRecordId: string) => {
  try {
    return instance
      .get(`${EndPoints.getMortuaryRecords}/${mortuaryRecordId}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMortuaryHistory = async (mortuaryRecordId: string) => {
  try {
    return instance
      .get(`${EndPoints.getMortuaryHistory}/${mortuaryRecordId}/history`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMortuaryRecordByBodyRemoval = async (bodyRemovalNumber: string) => {
  try {
    return instance
      .get(`${EndPoints.getMortuaryRecordByBodyRemoval}/${bodyRemovalNumber}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const registerMortuaryIntake = async (bodyRemovalNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.registerMortuaryIntake}/${bodyRemovalNumber}/intake`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateMortuaryMeasurement = async (mortuaryRecordId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.updateMortuaryMeasurement}/${mortuaryRecordId}/measurement`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const startMortuaryStage = async (
  mortuaryRecordId: string,
  stage: string,
  payload: any,
) => {
  try {
    return instance
      .post(`${EndPoints.startMortuaryStage}/${mortuaryRecordId}/stages/${stage}/start`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeMortuaryStage = async (
  mortuaryRecordId: string,
  stage: string,
  payload: any,
) => {
  try {
    return instance
      .post(`${EndPoints.completeMortuaryStage}/${mortuaryRecordId}/stages/${stage}/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getBodyDispatches = async ({
  dispatchType,
  status,
  searchText,
  page = 0,
  size = 10,
}: {
  dispatchType?: string;
  status?: string;
  searchText?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.getBodyDispatches, {
        params: {
          ...(dispatchType ? { dispatchType } : {}),
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getBodyDispatch = async (dispatchNumber: string) => {
  try {
    return instance
      .get(`${EndPoints.getBodyDispatches}/${dispatchNumber}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createInternalTransferDispatch = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.createInternalTransfer, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const submitInternalTransferDispatch = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.submitInternalTransfer}/${dispatchNumber}/internal-transfer/submit`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const requestMovementApproval = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.requestMovementApproval}/${dispatchNumber}/request-movement-approval`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const decideMovementApproval = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.decideMovementApproval}/${dispatchNumber}/decide-movement-approval`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const acknowledgeBodyMovement = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.acknowledgeMovement}/${dispatchNumber}/acknowledge-movement`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const receiveInternalTransferDispatch = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.receiveInternalTransfer}/${dispatchNumber}/internal-transfer/receive`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createExternalParlourRelease = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.createExternalParlourRelease, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const releaseExternalParlour = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.releaseExternalParlour}/${dispatchNumber}/external-parlour/release`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createFamilyDeliveryDispatch = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.createFamilyDelivery, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeFamilyDeliveryDispatch = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.completeFamilyDelivery}/${dispatchNumber}/family-delivery/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const markBodyDispatchEasipolUploaded = async (dispatchNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.markBodyDispatchEasipolUploaded}/${dispatchNumber}/easipol-upload`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const downloadOperationalSignature = async (fileKey: string) => {
  try {
    return instance
      .get(EndPoints.downloadOperationalSignature, {
        params: { fileKey },
        responseType: "blob",
        headers: {
          Accept: "*/*",
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehousePreparations = async ({
  status,
  branch,
  searchText,
  page = 0,
  size = 10,
}: {
  status?: string;
  branch?: string;
  searchText?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.getWarehousePreparations, {
        params: {
          ...(status ? { status } : {}),
          ...(branch ? { branch } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFuneralPackageCatalog = async (sourceType?: "MANUAL" | "POLICY") => {
  try {
    return instance
      .get(EndPoints.getFuneralPackageCatalog, {
        params: sourceType ? { sourceType } : {},
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMortuaryTemperatureLogs = async (mortuaryName: string) => {
  try {
    return instance
      .get(EndPoints.getMortuaryTemperatureLogs, { params: { mortuaryName } })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const recordMortuaryTemperatureLog = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getMortuaryTemperatureLogs, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMortuaryStorageUnits = async (mortuaryName?: string, status?: string) => {
  try {
    return instance
      .get(EndPoints.getMortuaryStorageUnits, {
        params: {
          ...(mortuaryName ? { mortuaryName } : {}),
          ...(status ? { status } : {}),
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMortuaryCapacities = async ({
  province,
  region,
  mortuaryName,
}: {
  province?: string;
  region?: string;
  mortuaryName?: string;
}) => {
  try {
    return instance
      .get(EndPoints.getMortuaryCapacities, {
        params: {
          ...(province ? { province } : {}),
          ...(region ? { region } : {}),
          ...(mortuaryName ? { mortuaryName } : {}),
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const upsertMortuaryStorageUnit = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getMortuaryStorageUnits, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMortuaryHygieneChecklists = async (mortuaryName: string) => {
  try {
    return instance
      .get(EndPoints.getMortuaryHygieneChecklists, { params: { mortuaryName } })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeMortuaryHygieneChecklist = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getMortuaryHygieneChecklists, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateMortuaryStage = async (
  mortuaryRecordId: string,
  stage: string,
  payload: any,
) => {
  try {
    return instance
      .put(`${EndPoints.updateMortuaryStage}/${mortuaryRecordId}/stages/${stage}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehousePreparation = async (preparationNumber: string) => {
  try {
    return instance
      .get(`${EndPoints.getWarehousePreparations}/${preparationNumber}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createWarehousePreparation = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getWarehousePreparations, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateWarehousePreparation = async (preparationNumber: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.getWarehousePreparations}/${preparationNumber}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const submitWarehousePreparation = async (preparationNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehousePreparations}/${preparationNumber}/submit`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const markWarehousePreparationReady = async (preparationNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehousePreparations}/${preparationNumber}/mark-ready`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehouseMaterialChecklists = async ({
  status,
  searchText,
  page = 0,
  size = 10,
}: {
  status?: string;
  searchText?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.getWarehouseMaterialChecklists, {
        params: {
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehouseMaterialChecklist = async (checklistNumber: string) => {
  try {
    return instance
      .get(`${EndPoints.getWarehouseMaterialChecklists}/${checklistNumber}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createWarehouseMaterialChecklist = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getWarehouseMaterialChecklists, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateWarehouseMaterialChecklist = async (checklistNumber: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.getWarehouseMaterialChecklists}/${checklistNumber}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const requestWarehouseDispatchApproval = async (checklistNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.requestWarehouseDispatchApproval}/${checklistNumber}/request-dispatch-approval`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const decideWarehouseDispatchApproval = async (checklistNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.decideWarehouseDispatchApproval}/${checklistNumber}/decide-dispatch-approval`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const confirmWarehouseMaterialDispatch = async (checklistNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseMaterialChecklists}/${checklistNumber}/confirm-dispatch`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const confirmWarehouseMaterialReturn = async (checklistNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseMaterialChecklists}/${checklistNumber}/confirm-return`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const reconcileWarehouseOutstandingMaterials = async (checklistNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseMaterialChecklists}/${checklistNumber}/reconcile-outstanding`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const triggerWarehouseLossReport = async (checklistNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseMaterialChecklists}/${checklistNumber}/trigger-loss-report`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehouseBrokenLossReports = async ({
  status,
  searchText,
  page = 0,
  size = 10,
}: {
  status?: string;
  searchText?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.getWarehouseBrokenLossReports, {
        params: {
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehouseBrokenLossReport = async (reportNumber: string) => {
  try {
    return instance
      .get(`${EndPoints.getWarehouseBrokenLossReports}/${reportNumber}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createWarehouseBrokenLossReport = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getWarehouseBrokenLossReports, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateWarehouseBrokenLossReport = async (reportNumber: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.getWarehouseBrokenLossReports}/${reportNumber}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const submitWarehouseBrokenLossReport = async (reportNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseBrokenLossReports}/${reportNumber}/submit`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getAllManufacturer = async () => {
  try {
    return instance.get(EndPoints.allManufacturer)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getAllModals = async (manufacturerId: string) => {
  try {
    return instance.get(EndPoints.allModals + "/" + manufacturerId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getFleetPortal = async () => {
  try {
    return instance
      .get(EndPoints.getFleetPortal)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFleetBranches = async (activeOnly = true) => {
  try {
    return instance
      .get(EndPoints.operationalLocations, { params: { activeOnly, capability: "FLEET" } })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getOperationalLocations = async (capability?: "BRANCH" | "MORTUARY" | "FLEET" | "WAREHOUSE", activeOnly = true) => {
  try {
    return instance
      .get(EndPoints.operationalLocations, { params: { activeOnly, capability } })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createOperationalLocation = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.operationalLocations, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateOperationalLocation = async (locationId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.operationalLocations}/${locationId}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getNearestFleetBranch = async (latitude: number, longitude: number) => {
  try {
    return instance
      .get(EndPoints.getNearestFleetBranch, { params: { latitude, longitude } })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getBodyRemovalResourceRecommendations = async (bodyRemovalNumber: string) => {
  try {
    return instance
      .get(`${EndPoints.getBodyRemovalResourceRecommendations}/${bodyRemovalNumber}/resource-recommendations`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const recordFleetMileage = async (vehicleId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.recordFleetMileage}/${vehicleId}/mileage`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFleetMileageHistory = async (vehicleId: string) => {
  try {
    return instance
      .get(`${EndPoints.recordFleetMileage}/${vehicleId}/mileage`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const syncCartrackMileage = async (vehicleId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.syncCartrackMileage}/${vehicleId}/cartrack-mileage`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createFleetBodyTransfer = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getFleetBodyTransfers, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateFleetBodyTransferStatus = async (transferId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.getFleetBodyTransfers}/${transferId}/status`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFleetBodyTransfers = async (page = 0, size = 10, branchLocation?: string) => {
  try {
    return instance
      .get(EndPoints.getFleetBodyTransfers, {
        params: { page, size, ...(branchLocation ? { branchLocation } : {}) },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFleetReportSummary = async () => {
  try {
    return instance
      .get(EndPoints.getFleetReportSummary)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFuneralPlanningByCase = async (caseId: string) => {
  try {
    return instance
      .get(`${EndPoints.getFuneralPlanningByCase}/${caseId}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFuneralPlans = async ({
  status,
  searchText,
  page = 0,
  size = 10,
}: {
  status?: string | null;
  searchText?: string | null;
  page?: number;
  size?: number;
} = {}) => {
  try {
    return instance
      .get(EndPoints.funeralPlanning, {
        params: {
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const ensureFuneralPlanningByCase = async (caseId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.ensureFuneralPlanningByCase}/${caseId}/ensure`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const syncAssignedFuneralPlans = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.syncAssignedFuneralPlans, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const saveFuneralPlanning = async (caseId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.updateFuneralPlanningByCase}/${caseId}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const markFuneralPlanningReady = async (planId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/mark-ready`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const confirmFuneralPlanning = async (planId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/confirm`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const triggerFuneralPlanningFeedback = async (planId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/trigger-feedback`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeFuneralPlanning = async (planId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const requestFuneralPlanningTask = async (planId: string, taskType: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/tasks/${taskType}/request`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeFuneralPlanningTask = async (planId: string, taskType: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/tasks/${taskType}/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFuneralPlanningTaskQueue = async (params: any) => {
  try {
    return instance
      .get(`${EndPoints.funeralPlanning}/tasks/queue`, { params })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFuneralHandoverChecklist = async (planId: string) => {
  try {
    return instance
      .get(`${EndPoints.funeralPlanning}/${planId}/handover-checklist`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const saveFuneralHandoverChecklist = async (planId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.funeralPlanning}/${planId}/handover-checklist`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeFuneralHandoverChecklist = async (planId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/handover-checklist/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const distributeFuneralHandoverChecklist = async (planId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/handover-checklist/distribute`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFuneralPurchaseOrderRequest = async (planId: string) => {
  try {
    return instance
      .get(`${EndPoints.funeralPlanning}/${planId}/purchase-order-request`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const requestFuneralPurchaseOrder = async (planId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/purchase-order-request`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const receiveFuneralPurchaseOrder = async (poRequestNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/purchase-order-requests/${poRequestNumber}/receive`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getSpecialServices = async ({
  serviceType,
  status,
  searchText,
  page = 0,
  size = 10,
}: {
  serviceType?: string | null;
  status?: string | null;
  searchText?: string | null;
  page?: number;
  size?: number;
} = {}) => {
  try {
    return instance
      .get(EndPoints.specialServices, {
        params: {
          ...(serviceType ? { serviceType } : {}),
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getSpecialService = async (serviceNumber: string) => {
  try {
    return instance
      .get(`${EndPoints.specialServices}/${serviceNumber}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createSpecialService = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.specialServices, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateSpecialService = async (serviceNumber: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.specialServices}/${serviceNumber}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const submitSpecialService = async (serviceNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.specialServices}/${serviceNumber}/submit`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const approveSpecialService = async (serviceNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.specialServices}/${serviceNumber}/approve`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const startSpecialServiceTransport = async (serviceNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.specialServices}/${serviceNumber}/start-transport`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeSpecialService = async (serviceNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.specialServices}/${serviceNumber}/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehouseStockTakes = async (status?: string) => {
  try {
    return instance
      .get(EndPoints.getWarehouseStockTakes, { params: status ? { status } : {} })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createWarehouseStockTake = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getWarehouseStockTakes, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const submitWarehouseStockTake = async (stockTakeNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseStockTakes}/${stockTakeNumber}/submit`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const convertCashClientToPolicyholder = async (caseId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.casesBase}/${caseId}/convert-to-policyholder`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error?.response || { status: 500, data: { message: "Server Error" } };
  }
};

export const updateWarehouseStockTakeCounts = async (stockTakeNumber: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.getWarehouseStockTakes}/${stockTakeNumber}/counts`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const reviewWarehouseStockTake = async (stockTakeNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseStockTakes}/${stockTakeNumber}/review`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getWarehouseCleaningRosters = async (status?: string) => {
  try {
    return instance
      .get(EndPoints.getWarehouseCleaningRosters, { params: status ? { status } : {} })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createWarehouseCleaningRoster = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getWarehouseCleaningRosters, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const startWarehouseCleaningRoster = async (rosterNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseCleaningRosters}/${rosterNumber}/start`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const completeWarehouseCleaningRoster = async (rosterNumber: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.getWarehouseCleaningRosters}/${rosterNumber}/complete`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getInventoryMaterials = async ({ branch, status, searchText }: { branch?: string; status?: string; searchText?: string }) => {
  try {
    return instance
      .get(EndPoints.getInventoryMaterials, {
        params: {
          ...(branch ? { branch } : {}),
          ...(status ? { status } : {}),
          ...(searchText ? { searchText } : {}),
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const lookupInventoryMaterials = async ({
  branch,
  query,
  page = 0,
  size = 25,
}: {
  branch: string;
  query?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.lookupInventoryMaterials, {
        params: { branch, ...(query ? { query } : {}), page, size },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const resolveInventoryMaterials = async (
  branch: string,
  items: Array<{ index: number; description: string }>,
) => {
  try {
    return instance
      .post(EndPoints.resolveInventoryMaterials, { branch, items })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getInventoryDashboard = async (branch?: string) => {
  try {
    return instance
      .get(EndPoints.getInventoryDashboard, { params: branch ? { branch } : {} })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getInventoryMovements = async (branch?: string) => {
  try {
    return instance
      .get(EndPoints.getInventoryMovements, { params: branch ? { branch } : {} })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createInventoryMaterial = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.getInventoryMaterials, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const followUpFuneralPlanningTask = async (planId: string, taskType: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.funeralPlanning}/${planId}/tasks/${taskType}/follow-up`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getInventoryMovementHistory = async (params: {
  branch?: string;
  movementType?: string;
  itemCode?: string;
  performedBy?: string;
  searchText?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.getInventoryMovementHistory, {
        params: Object.fromEntries(Object.entries(params).filter(([, value]) => value !== "" && value !== undefined)),
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const previewInventoryImport = async (file: File) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    return instance
      .post(EndPoints.previewInventoryImport, formData)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const importInventoryMaterials = async (file: File, performedBy?: string) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    return instance
      .post(EndPoints.importInventoryMaterials, formData, {
        params: performedBy ? { performedBy } : {},
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const updateInventoryMaterial = async (itemCode: string, warehouseBranch: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.getInventoryMaterials}/${encodeURIComponent(itemCode)}`, payload, {
        params: { warehouseBranch },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const adjustInventoryMaterial = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.adjustInventoryMaterial, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialDashboard = async () => {
  try {
    return instance
      .get(EndPoints.memorialDashboard)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialReports = async () => {
  try {
    return instance
      .get(EndPoints.memorialReports)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getFilteredMemorialReports = async (params: any = {}) => {
  try {
    return instance
      .get(EndPoints.memorialReports, { params })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialLocations = async () => {
  try {
    return instance
      .get(EndPoints.memorialLocations)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createMemorialLocation = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.memorialLocations, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialPlots = async (locationId?: string) => {
  try {
    return instance
      .get(EndPoints.memorialPlots, {
        params: locationId ? { locationId } : {},
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createMemorialPlot = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.memorialPlots, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const saveMemorialTimeSlot = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.memorialTimeSlots, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createMemorialEnquiry = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.memorialEnquiries, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialEnquiries = async ({ page = 0, size = 50 }: { page?: number; size?: number } = {}) => {
  try {
    return instance
      .get(EndPoints.memorialEnquiries, { params: { page, size } })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialAvailableSlots = async (locationId: string, date: string) => {
  try {
    return instance
      .get(EndPoints.memorialAvailableSlots, { params: { locationId, date } })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const requestMemorialAppointment = async (payload: any, userId?: string) => {
  try {
    return instance
      .post(EndPoints.memorialAppointments, payload, {
        headers: userId ? { "user-Id": userId } : {},
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialAppointments = async ({
  fromDate,
  toDate,
  page = 0,
  size = 10,
}: {
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.memorialAppointments, {
        params: {
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const approveMemorialAppointment = async (appointmentId: string, payload: any) => postMemorialAppointmentAction(appointmentId, "approve", payload);

export const rejectMemorialAppointment = async (appointmentId: string, payload: any) => postMemorialAppointmentAction(appointmentId, "reject", payload);

export const rescheduleMemorialAppointment = async (appointmentId: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.memorialAppointments}/${appointmentId}/reschedule`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const createMemorialApplication = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.memorialApplications, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialApplications = async ({
  status,
  financeStatus,
  certificateStatus,
  branchLocation,
  locationId,
  page = 0,
  size = 10,
}: {
  status?: string;
  financeStatus?: string;
  certificateStatus?: string;
  branchLocation?: string;
  locationId?: string;
  page?: number;
  size?: number;
}) => {
  try {
    return instance
      .get(EndPoints.memorialApplications, {
        params: {
          ...(status ? { status } : {}),
          ...(financeStatus ? { financeStatus } : {}),
          ...(certificateStatus ? { certificateStatus } : {}),
          ...(branchLocation ? { branchLocation } : {}),
          ...(locationId ? { locationId } : {}),
          page,
          size,
        },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getMemorialApplication = async (applicationId: string) => {
  try {
    return instance
      .get(`${EndPoints.memorialApplications}/${applicationId}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const submitMemorialApplication = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "submit", payload);

export const verifyMemorialApplication = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "verify", payload);

export const returnMemorialVerification = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "verification/return", payload);

export const uploadMemorialDocument = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "documents", payload);

export const generateMemorialInvoice = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "invoice", payload);

export const uploadMemorialProofOfPayment = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "proof-of-payment", payload);

export const approveMemorialFinance = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "finance/approve", payload);

export const rejectMemorialFinance = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "finance/reject", payload);

export const returnMemorialFinance = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "finance/return", payload);

export const requestMemorialPaymentInfo = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "finance/request-info", payload);

export const generateMemorialCertificate = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "certificate/generate", payload);

export const approveMemorialCertificate = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "certificate/approve", payload);

export const issueMemorialCertificate = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "certificate/issue", payload);

export const recordMemorialBurialVerification = async (applicationId: string, payload: any) => postMemorialApplicationAction(applicationId, "burial-verification", payload);

export const allocateMemorialPlot = async (applicationId: string, plotId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.memorialApplications}/${applicationId}/allocate-plot/${plotId}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const allocateAvailableMemorialPlot = async (applicationId: string, locationId: string, payload: any) => {
  try {
    return instance
      .post(`${EndPoints.memorialApplications}/${applicationId}/allocate-available-plot/${locationId}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const postMemorialAppointmentAction = async (
  appointmentId: string,
  actionPath: string,
  payload: any,
) => {
  try {
    return instance
      .post(`${EndPoints.memorialAppointments}/${appointmentId}/${actionPath}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const postMemorialApplicationAction = async (
  applicationId: string,
  actionPath: string,
  payload: any,
) => {
  try {
    return instance
      .post(`${EndPoints.memorialApplications}/${applicationId}/${actionPath}`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};
